// src/app/components/reporting/components/report-output.tsx
import { useState } from 'react';
import {
  Sparkles, CheckCircle2, Users, AlertCircle,
  ChevronDown, ChevronUp, Pencil, Check, X, Trash2, Plus, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { updateReport } from '@/app/services/reporting/insights-api-service';
import { ExportActions } from './export-actions';
import type { GeneratedReport, ReportSection } from '../types';

// ── Editable field ────────────────────────────────────────────────────────────

function Editable({ value, onChange, multiline = false, placeholder = '' }: {
  value: string; onChange: (v: string) => void; multiline?: boolean; placeholder?: string;
}) {
  const style: React.CSSProperties = {
    backgroundColor: 'var(--background)', border: '1px solid var(--alpha-teal)',
    borderRadius: '8px', padding: '8px 10px', fontSize: 'var(--text-sm)',
    lineHeight: '1.6', width: '100%', outline: 'none',
  };
  return multiline
    ? <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4} style={{ ...style, resize: 'vertical' }} />
    : <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={style} />;
}

// ── Section edit card ─────────────────────────────────────────────────────────

function EditSectionCard({ section, index, total, onChange, onDelete, onMoveUp, onMoveDown }: {
  section: ReportSection; index: number; total: number;
  onChange: (s: ReportSection) => void; onDelete: () => void; onMoveUp: () => void; onMoveDown: () => void;
}) {
  return (
    <div
      className="p-3 md:p-4 rounded-xl space-y-3"
      style={{ backgroundColor: 'var(--background)', border: '1px solid var(--alpha-teal)' }}
    >
      {/* Control row — stacks cleanly on mobile (number + arrows on top,
          title + metric + delete below); single row on sm+ */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        {/* Number + move arrows */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
            style={{
              backgroundColor: 'var(--alpha-teal-10)',
              color: 'var(--alpha-teal)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
            }}
          >
            {index + 1}
          </div>
          <div className="flex flex-col gap-0.5">
            {[['up', onMoveUp, index === 0], ['down', onMoveDown, index === total - 1]].map(([dir, fn, dis]) => (
              <button
                key={dir as string}
                onClick={fn as () => void}
                disabled={dis as boolean}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: dis ? 'default' : 'pointer',
                  opacity: dis ? 0.3 : 1,
                  padding: '2px',
                }}
              >
                {dir === 'up'
                  ? <ChevronUp className="w-3.5 h-3.5" style={{ color: 'var(--muted-foreground)' }} />
                  : <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--muted-foreground)' }} />}
              </button>
            ))}
          </div>
        </div>

        {/* Title — takes remaining width */}
        <div className="flex-1 min-w-0">
          <Editable
            value={section.title}
            onChange={(v) => onChange({ ...section, title: v })}
            placeholder="Section title"
          />
        </div>

        {/* Metric input + delete — side by side on mobile (avoids metric
            input going full-width), inline on sm+ */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            type="text"
            value={section.key_metric || ''}
            onChange={(e) => onChange({ ...section, key_metric: e.target.value || null })}
            placeholder="Metric"
            className="flex-1 sm:flex-initial"
            style={{
              width: '100%',
              minWidth: '90px',
              maxWidth: '110px',
              padding: '8px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--background)',
              fontSize: 'var(--text-sm)',
              outline: 'none',
            }}
          />
          <button
            onClick={onDelete}
            style={{
              background: 'none',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
              color: 'var(--infrared-coral)',
              flexShrink: 0,
            }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div>
        <p
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--muted-foreground)',
            marginBottom: '4px',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          Content
        </p>
        <Editable
          value={section.content}
          onChange={(v) => onChange({ ...section, content: v })}
          multiline
          placeholder="Section content..."
        />
      </div>
      <div>
        <p
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--muted-foreground)',
            marginBottom: '4px',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          Reasoning
        </p>
        <Editable
          value={section.reasoning}
          onChange={(v) => onChange({ ...section, reasoning: v })}
          placeholder="Why this section is included"
        />
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

interface ReportOutputProps {
  report:            GeneratedReport;
  insightCount:      number;
  onReportUpdated?:  (updated: GeneratedReport) => void;
}

export function ReportOutput({ report, insightCount, onReportUpdated }: ReportOutputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving,  setIsSaving]  = useState(false);

  const [editTitle,   setEditTitle]   = useState(report.title);
  const [editSummary, setEditSummary] = useState(report.executive_summary);
  const [editSections,        setEditSections]        = useState<ReportSection[]>(report.sections);
  const [editFindings,        setEditFindings]        = useState<string[]>(report.key_findings);
  const [editRecommendations, setEditRecommendations] = useState<string[]>(report.recommendations);

  const resetEdit = () => {
    setEditTitle(report.title); setEditSummary(report.executive_summary);
    setEditSections(report.sections); setEditFindings(report.key_findings);
    setEditRecommendations(report.recommendations);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const output = {
        title: editTitle, audience: report.audience, format: report.format,
        confidence: report.confidence, executive_summary: editSummary,
        sections: editSections, key_findings: editFindings,
        recommendations: editRecommendations, insight_count: report.insight_count,
      };
      const updated = await updateReport(report.id, output);
      onReportUpdated?.(updated);
      toast.success('Report saved');
      setIsEditing(false);
    } catch { toast.error('Save failed — please try again'); }
    finally  { setIsSaving(false); }
  };

  const updateSection = (i: number, s: ReportSection) => setEditSections((p) => p.map((x, j) => (j === i ? s : x)));
  const deleteSection = (i: number) => setEditSections((p) => p.filter((_, j) => j !== i));
  const moveSection   = (i: number, dir: 'up' | 'down') => setEditSections((p) => {
    const a = [...p]; const t = dir === 'up' ? i - 1 : i + 1;
    if (t < 0 || t >= a.length) return a; [a[i], a[t]] = [a[t], a[i]]; return a;
  });

  const displaySections = isEditing ? editSections : report.sections;
  const displayFindings = isEditing ? editFindings : report.key_findings;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: 'var(--card)',
        border: isEditing ? '2px solid var(--alpha-teal)' : '1px solid var(--border)',
        boxShadow: 'var(--elevation-sm)',
        transition: 'border-color 0.2s',
      }}
    >

      {/* Header */}
      <div
        className="p-4 md:p-6"
        style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--alpha-teal-5)' }}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: 'var(--alpha-teal)' }}
          >
            <Sparkles className="w-5 h-5" style={{ color: 'white' }} />
          </div>
          <div className="flex-1 min-w-0">
            {isEditing
              ? <Editable value={editTitle} onChange={setEditTitle} placeholder="Report title" />
              : (
                <h3
                  style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-weight-semibold)',
                    marginBottom: '2px',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {report.title}
                </h3>
              )}
            <p
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)',
                marginTop: '4px',
              }}
            >
              {displaySections.length} sections · {report.confidence}% confidence · {report.format}
            </p>
          </div>
        </div>

        <div
          className="p-3 rounded-lg space-y-2 mb-4"
          style={{ backgroundColor: 'var(--background)' }}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--alpha-teal)' }} />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              Based on {report.insight_count} saved insights
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--signal-blue)' }} />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              Adapted for {report.audience}
            </span>
          </div>
        </div>

        <div
          className="p-3 md:p-4 rounded-lg"
          style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
        >
          <p
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '6px',
            }}
          >
            Executive Summary
          </p>
          {isEditing
            ? <Editable value={editSummary} onChange={setEditSummary} multiline placeholder="Executive summary..." />
            : (
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  lineHeight: '1.6',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {report.executive_summary}
              </p>
            )}
        </div>
      </div>

      {/* Sections */}
      <div className="p-4 md:p-6 space-y-4">
        {isEditing && (
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--alpha-teal)',
              fontWeight: 'var(--font-weight-semibold)',
            }}
          >
            Edit Mode — reorder, edit, add or remove sections
          </p>
        )}
        {displaySections.map((section, idx) =>
          isEditing ? (
            <EditSectionCard
              key={idx}
              section={section}
              index={idx}
              total={editSections.length}
              onChange={(s) => updateSection(idx, s)}
              onDelete={() => deleteSection(idx)}
              onMoveUp={() => moveSection(idx, 'up')}
              onMoveDown={() => moveSection(idx, 'down')}
            />
          ) : (
            <div
              key={idx}
              className="p-3 md:p-4 rounded-xl"
              style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: 'var(--alpha-teal-10)',
                    color: 'var(--alpha-teal)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                  }}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4
                      style={{
                        fontWeight: 'var(--font-weight-semibold)',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {section.title}
                    </h4>
                    {section.key_metric && (
                      <span
                        className="px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: 'var(--alpha-teal-10)',
                          color: 'var(--alpha-teal)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-semibold)',
                        }}
                      >
                        {section.key_metric}
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--muted-foreground)',
                      lineHeight: '1.6',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {section.content}
                  </p>
                </div>
              </div>
              {/* Reasoning box — no longer uses fixed pl-11 (breaks on mobile) */}
              <div
                className="p-3 rounded-lg"
                style={{ backgroundColor: 'var(--spectral-violet-5)' }}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle
                    className="w-4 h-4 shrink-0 mt-0.5"
                    style={{ color: 'var(--spectral-violet)' }}
                  />
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--spectral-violet)',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>AI Reasoning: </span>
                    {section.reasoning}
                  </p>
                </div>
              </div>
            </div>
          )
        )}
        {isEditing && (
          <button
            onClick={() => setEditSections((p) => [...p, { title: 'New Section', content: '', key_metric: null, source_insight_ids: [], reasoning: '' }])}
            className="w-full py-3 rounded-xl flex items-center justify-center gap-2"
            style={{
              border: '2px dashed var(--border)',
              backgroundColor: 'transparent',
              color: 'var(--muted-foreground)',
              fontSize: 'var(--text-sm)',
              cursor: 'pointer',
            }}
          >
            <Plus className="w-4 h-4" /> Add Section
          </button>
        )}
      </div>

      {/* Key Findings */}
      {displayFindings.length > 0 && (
        <div className="px-4 md:px-6 pb-4">
          <h4
            style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-semibold)',
              marginBottom: '10px',
            }}
          >
            Key Findings
          </h4>
          <div className="space-y-2">
            {displayFindings.map((f, i) =>
              isEditing ? (
                <div key={i} className="flex items-center gap-2">
                  <span style={{ color: 'var(--alpha-teal)', minWidth: '16px', flexShrink: 0 }}>•</span>
                  <input
                    type="text"
                    value={f}
                    onChange={(e) => setEditFindings((p) => p.map((x, j) => j === i ? e.target.value : x))}
                    placeholder="Key finding..."
                    className="flex-1 min-w-0"
                    style={{
                      padding: '6px 10px',
                      borderRadius: '8px',
                      border: '1px solid var(--alpha-teal)',
                      backgroundColor: 'var(--background)',
                      fontSize: 'var(--text-sm)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => setEditFindings((p) => p.filter((_, j) => j !== i))}
                    className="flex-shrink-0"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <Trash2 className="w-4 h-4" style={{ color: 'var(--infrared-coral)' }} />
                  </button>
                </div>
              ) : (
                <div key={i} className="flex items-start gap-2">
                  <span style={{ color: 'var(--alpha-teal)', marginTop: '2px', flexShrink: 0 }}>›</span>
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      lineHeight: '1.5',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {f}
                  </p>
                </div>
              )
            )}
            {isEditing && (
              <button
                onClick={() => setEditFindings((p) => [...p, ''])}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--alpha-teal)',
                  fontSize: 'var(--text-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Plus className="w-3.5 h-3.5" /> Add Finding
              </button>
            )}
          </div>
        </div>
      )}

      {/* Edit / Save / Cancel — stacks on mobile */}
      <div className="px-4 md:px-6 pb-4">
        {isEditing ? (
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-3 rounded-lg flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'var(--alpha-teal)',
                color: 'white',
                fontWeight: 'var(--font-weight-semibold)',
                border: 'none',
                cursor: isSaving ? 'wait' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
              }}
            >
              {isSaving
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : <><Check className="w-4 h-4" /> Save Changes</>}
            </button>
            <button
              onClick={() => { resetEdit(); setIsEditing(false); }}
              disabled={isSaving}
              className="flex-1 py-3 rounded-lg flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontWeight: 'var(--font-weight-semibold)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="w-full py-3 rounded-lg flex items-center justify-center gap-2 transition-all hover:opacity-90"
            style={{
              backgroundColor: 'var(--graphite)',
              color: 'white',
              fontWeight: 'var(--font-weight-semibold)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Pencil className="w-4 h-4" /> Review &amp; Edit Story
          </button>
        )}
      </div>

      {/* Export */}
      <div
        className="p-4 md:p-6"
        style={{ borderTop: '1px solid var(--border)', backgroundColor: 'var(--muted)' }}
      >
        <h4
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-weight-semibold)',
            marginBottom: '4px',
          }}
        >
          Export &amp; Share
        </h4>
        <p
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)',
            marginBottom: '16px',
          }}
        >
          Download, share, or syndicate your report
        </p>
        <ExportActions report={report} />
      </div>
    </div>
  );
}